import { createSignal, For, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { Portal } from "solid-js/web";
import { TransitionGroup } from "@fleet/ui";
import { AlertCircle, CheckCircle2, Info, X } from "@fleet/icons";

import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { vibrate } from "@/lib/gestures/haptics";
import { TOAST_SWIPE } from "@/lib/gestures/presets";

type ToastType = "success" | "error" | "info" | "warning";

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

const TOAST_DURATION = 5000;
const RADIX_36 = 36;
const ID_LENGTH = 9;
// oxlint-disable-next-line lingui/no-unlocalized-strings -- CSS media query, not UI copy
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandLimitPx: RUBBER_BAND_LIMIT,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  hapticMs: HAPTIC_MS,
  dismissAnimationMs: DISMISS_ANIMATION_MS,
  cancelAnimationMs: CANCEL_ANIMATION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
} = TOAST_SWIPE;

const [toasts, setToasts] = createStore<Toast[]>([]);
// Auto-dismiss timers, keyed by toast id, so a manual/gesture close can cancel
// the pending timeout and a swipe-in-progress can pause it.
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

function clearTimer(id: string) {
  const timer = timers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    timers.delete(id);
  }
}

function removeToast(id: string) {
  clearTimer(id);
  setToasts((prev) => prev.filter((t) => t.id !== id));
}

function scheduleDismiss(id: string) {
  clearTimer(id);
  timers.set(
    id,
    setTimeout(() => {
      removeToast(id);
    }, TOAST_DURATION),
  );
}

export function showToast(message: string, type: ToastType = "info") {
  const id = Math.random().toString(RADIX_36).slice(2, ID_LENGTH);
  setToasts((prev) => [...prev, { id, message, type }]);
  scheduleDismiss(id);
}

function ToastItem(props: { toast: Toast }) {
  const [tx, setTx] = createSignal(0);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);

  let surfaceRef: HTMLElement | undefined;

  const settle = (ms: number) => {
    setTx(0);
    globalThis.setTimeout(() => {
      setIsAnimating(false);
    }, ms);
  };

  const dismissOff = (toRight: boolean) => {
    if (prefersReducedMotion()) {
      removeToast(props.toast.id);
      return;
    }
    const width = surfaceRef?.clientWidth ?? FALLBACK_WIDTH_PX;
    const offset = width + RUBBER_BAND_LIMIT;
    setIsAnimating(true);
    setTx(toRight ? offset : -offset);
    const { id } = props.toast;
    globalThis.setTimeout(() => {
      removeToast(id);
    }, DISMISS_ANIMATION_MS);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    haptics: true,
    hapticMs: HAPTIC_MS,
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
      // Pause auto-dismiss while the user is interacting with the toast.
      clearTimer(props.toast.id);
    },
    onMove: (sample) => {
      setTx(applyRubberBand(sample.dx, RUBBER_BAND_LIMIT, RUBBER_BAND_FACTOR));
    },
    onEnd: (sample) => {
      setIsSwiping(false);
      const finalTx = tx();
      const committed =
        finalTx > COMMIT_THRESHOLD_PX ||
        finalTx < -COMMIT_THRESHOLD_PX ||
        (sample.isFling && sample.direction !== null);

      if (committed) {
        vibrate(HAPTIC_MS);
        // Fling direction wins; otherwise follow the drag's sign.
        const toRight = sample.direction === "right" || (sample.direction === null && finalTx > 0);
        dismissOff(toRight);
        return;
      }
      // Not enough travel: snap back and restart the auto-dismiss countdown.
      setIsAnimating(true);
      settle(CANCEL_ANIMATION_MS);
      scheduleDismiss(props.toast.id);
    },
    onCancel: () => {
      setIsSwiping(false);
      setIsAnimating(true);
      settle(CANCEL_ANIMATION_MS);
      scheduleDismiss(props.toast.id);
    },
  });

  const handlers = pointerHandlers(recognizer);

  const transform = () => `translate3d(${tx()}px, 0, 0)`;
  const transition = () =>
    isAnimating() ? `transform ${CANCEL_ANIMATION_MS}ms ease-out, opacity 150ms ease-out` : "none";
  // Fade the toast as it slides toward dismissal for a polished feel.
  const opacity = () => {
    const distance = Math.abs(tx());
    if (distance <= COMMIT_THRESHOLD_PX) {
      return 1;
    }
    return Math.max(0.25, 1 - (distance - COMMIT_THRESHOLD_PX) / (FALLBACK_WIDTH_PX / 2));
  };

  return (
    <div
      ref={(el) => {
        surfaceRef = el;
      }}
      // CC-30: error toasts are announced ASSERTIVELY (`role="alert"`
      // + `aria-live="assertive"`) so a screen reader interrupts on the spot —
      // a failure (4xx/5xx that `handleApiResponse` already turns into a
      // visible error) must never stay silent for screen-reader users either.
      // The rest (success/info/warning) stays "polite".
      role={props.toast.type === "error" ? "alert" : "status"}
      aria-live={props.toast.type === "error" ? "assertive" : "polite"}
      data-testid="toast"
      // e2e anchor: distinguishes an ERROR notice from a success one without
      // depending on the translated text. A test that only counts
      // `[data-testid=toast]` cannot prove that a FAILURE was reported.
      data-toast-type={props.toast.type}
      class="flex items-center justify-between gap-3 rounded-lg border bg-popover p-4 shadow-lg will-change-transform select-none"
      classList={{
        "border-success/30 text-success": props.toast.type === "success",
        "border-destructive/30 text-destructive": props.toast.type === "error",
        "border-warning/30 text-warning": props.toast.type === "warning",
        "border-primary/30 text-primary": props.toast.type === "info",
        "cursor-grabbing": isSwiping(),
      }}
      style={{
        "touch-action": "pan-y",
        transform: transform(),
        transition: transition(),
        opacity: opacity(),
      }}
      {...handlers}
    >
      <div class="flex min-w-0 items-center gap-3">
        <Show when={props.toast.type === "success"}>
          <CheckCircle2 class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "error"}>
          <AlertCircle class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "warning"}>
          <AlertCircle class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "info"}>
          <Info class="h-5 w-5 shrink-0" />
        </Show>
        <span class="min-w-0 break-words text-sm font-medium">{props.toast.message}</span>
      </div>
      <button
        type="button"
        onClick={() => {
          removeToast(props.toast.id);
        }}
        class="shrink-0 rounded-full p-1 transition-colors hover:bg-black/5"
      >
        <X class="h-4 w-4" />
      </button>
    </div>
  );
}

export function Toaster() {
  return (
    <Portal>
      <div
        class="pointer-events-none fixed z-[300] flex flex-col gap-2"
        style={{
          // Anchored bottom-right on desktop; full-width on mobile via insets.
          // Safe-area insets keep toasts clear of the notch / home indicator and
          // the layout never overflows horizontally (left+right both inset).
          left: "max(env(safe-area-inset-left), 1rem)",
          right: "max(env(safe-area-inset-right), 1rem)",
          bottom: "calc(1rem + env(safe-area-inset-bottom))",
          // Cap width and pin to the right edge on wider screens; the left
          // `max(...)` keeps it from spanning the whole viewport on desktop.
          "max-width": "420px",
          "margin-left": "auto",
        }}
      >
        <TransitionGroup name="toast">
          <For each={toasts}>
            {(toast) => (
              <div class="pointer-events-auto">
                <ToastItem toast={toast} />
              </div>
            )}
          </For>
        </TransitionGroup>
      </div>
    </Portal>
  );
}
